import { computed, inject, Injectable, signal } from "@angular/core";
import {
  catchError,
  finalize,
  Observable,
  of,
  shareReplay,
  tap,
} from "rxjs";
import { isUserOnboardingInProgress } from "src/app/pages/profile/profile.model";
import { ProfileService } from "src/app/pages/profile/profile.service";
import { extractSupportErrorMessage } from "src/app/shared/helpers/contact-support-history.helper";
import { BillingServiceOverview } from "./billing-service-overview.model";
import { BillingServiceOverviewService } from "./billing-service-overview.service";

/**
 * Single source of truth for billing service overview data shown in the
 * dashboard widget, usage dialog, minimized float, and pinned sidenav.
 */
@Injectable({
  providedIn: "root",
})
export class BillingServiceOverviewStore {
  private readonly overviewApi = inject(BillingServiceOverviewService);
  private readonly profileService = inject(ProfileService);

  private loadRequest$: Observable<BillingServiceOverview | null> | null = null;
  private refreshRequest$: Observable<BillingServiceOverview | null> | null = null;

  readonly overview = signal<BillingServiceOverview | null>(null);
  readonly loading = signal(false);
  readonly refreshing = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly lastUpdated = signal<Date | null>(null);

  readonly hasLoadError = computed(
    () => !!this.errorMessage() && !this.overview(),
  );

  readonly isBusy = computed(() => this.loading() || this.refreshing());

  /**
   * Fetches from API and updates the shared cache.
   * Call on dashboard route entry and when opening the usage modal.
 * Also used when a visible floating widget restores with an empty cache.
   */
  load(): Observable<BillingServiceOverview | null> {
    if (this.shouldSkipServiceOverviewFetch()) {
      return of(null);
    }

    if (this.loadRequest$) {
      return this.loadRequest$;
    }

    this.loading.set(true);
    this.errorMessage.set(null);

    this.loadRequest$ = this.overviewApi.getServiceOverview().pipe(
      tap((overview) => this.applySuccess(overview)),
      catchError((error) => {
        this.errorMessage.set(extractSupportErrorMessage(error));
        return of(null);
      }),
      finalize(() => {
        this.loading.set(false);
        this.loadRequest$ = null;
      }),
      shareReplay(1),
    );

    return this.loadRequest$;
  }

  /**
   * GET /billing/service-overview; clears in-flight dedupe so a new request always runs.
   */
  forceLoadFromApi(): Observable<BillingServiceOverview | null> {
    this.loadRequest$ = null;
    this.refreshRequest$ = null;
    return this.refresh();
  }

  /** GET /billing/service-overview and update the shared overview cache. */
  refresh(): Observable<BillingServiceOverview | null> {
    if (this.shouldSkipServiceOverviewFetch()) {
      return of(null);
    }

    if (this.refreshRequest$) {
      return this.refreshRequest$;
    }

    this.refreshing.set(true);
    this.errorMessage.set(null);

    this.refreshRequest$ = this.overviewApi.getServiceOverview().pipe(
      tap((overview) => this.applySuccess(overview)),
      catchError((error) => {
        this.errorMessage.set(extractSupportErrorMessage(error));
        return of(null);
      }),
      finalize(() => {
        this.refreshing.set(false);
        this.refreshRequest$ = null;
      }),
      shareReplay(1),
    );

    return this.refreshRequest$;
  }

  reset(): void {
    this.loadRequest$ = null;
    this.refreshRequest$ = null;
    this.overview.set(null);
    this.loading.set(false);
    this.refreshing.set(false);
    this.errorMessage.set(null);
    this.lastUpdated.set(null);
  }

  private applySuccess(overview: BillingServiceOverview): void {
    this.overview.set(overview);
    this.errorMessage.set(null);
    this.lastUpdated.set(new Date());
  }

  /** Skip GET /billing/service-overview during first-time onboarding. */
  private shouldSkipServiceOverviewFetch(): boolean {
    return isUserOnboardingInProgress(this.profileService.userProfile());
  }
}
